import React, { useRef, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faMars,
  faVenus,
  faPrint,
} from "@fortawesome/free-solid-svg-icons";
import { faFileLines } from "@fortawesome/free-solid-svg-icons";
import { Button } from "@mmfestival/shared-components";

// Fungsi untuk mengkapitalkan huruf pertama setiap kata
const capitalizeWords = (str) => {
  if (!str || typeof str !== "string") return "";
  return str
    .trim()
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Fungsi untuk mengonversi kode CIO atau nama negara ke kode ISO untuk bendera
const getFlagCode = (nationality) => {
  if (!nationality) return "un";
  const trimmed = String(nationality).trim();
  if (/^[A-Za-z]{2}$/.test(trimmed)) return trimmed.toLowerCase();
  const codeMap = {
    INA: "id",
    INDONESIA: "id",
    GERMANY: "de",
    RUSSIA: "ru",
    FRANCE: "fr",
    USA: "us",
    GBR: "gb",
    MALAYSIA: "my",
    MY: "my",
    JAPAN: "jp",
    JPN: "jp",
    "SOUTH KOREA": "kr",
    KOR: "kr",
    KOREA: "kr",
    THAILAND: "th",
    THA: "th",
    ITALY: "it",
    ITA: "it",
    SINGAPORE: "sg",
    SGP: "sg",
    AUSTRALIA: "au",
    AUS: "au",
    CHINA: "cn",
    CHN: "cn",
    BRUNEI: "bn",
    BRU: "bn",
    PHILIPPINES: "ph",
    PHI: "ph",
    SPAIN: "es",
    ESP: "es",
    NETHERLANDS: "nl",
    NED: "nl",
    SWITZERLAND: "ch",
    SUI: "ch",
    SWEDEN: "se",
    SWE: "se",
    BELGIUM: "be",
    BEL: "be",
    INDIA: "in",
    IND: "in",
    "UNITED KINGDOM": "gb",
    UK: "gb",
    NORWAY: "no",
    NOR: "no",
    EGYPT: "eg",
    EGY: "eg",
    CZECH: "cz",
    "CZECH REPUBLIC": "cz",
    CZE: "cz",
    MOROCCO: "ma",
    MAR: "ma",
    "SINT MAARTEN (DUTCH PART)": "sx",
    SXM: "sx",
    TAIWAN: "tw",
    TWN: "tw",
    BELARUS: "by",
    BLR: "by",
  };
  return codeMap[nationality.toUpperCase()] || "un";
};

// Fungsi untuk mengonversi gender ke format dengan ikon
const formatGender = (gender, isMixGroup) => {
  if (isMixGroup) {
    return (
      <>
        <FontAwesomeIcon icon={faMars} className="mr-1" />
        <FontAwesomeIcon icon={faVenus} className="mr-1" />
        MIX
      </>
    );
  }
  if (!gender) return "-";
  if (gender === "M" || gender === "Laki-laki")
    return (
      <>
        <FontAwesomeIcon icon={faMars} className="mr-1" /> Male
      </>
    );
  if (gender === "F" || gender === "Perempuan")
    return (
      <>
        <FontAwesomeIcon icon={faVenus} className="mr-1" /> Female
      </>
    );
  return gender;
};

export const ResultsDetail = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const canvasRef = useRef(null);
  const selectedEvent = (eventId || state?.eventId || "JMM").toUpperCase();

  // Scroll to top when the component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Jika tidak ada state, kembalikan ke halaman results
  if (!state || !state.name || !state.ranking) {
    navigate(`/results/${selectedEvent}`);
    return null;
  }

  const {
    name,
    ranking,
    category,
    categoryDisplay,
    startDateTime,
    bibNumber,
    nationality,
    gender,
    status,
    time,
    genderRanking,
    certificateUrl,
    backTo,
  } = state;

  const handlePrintCertificate = () => {
    if (certificateUrl) {
      window.open(certificateUrl, "_blank", "noopener,noreferrer");
      return;
    }
    generateCertificate();
  };

  const generateCertificate = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      console.error("Canvas tidak tersedia");
      return;
    }

    const ctx = canvas.getContext("2d");
    const certificateCategory = categoryDisplay || category || "-";

    const drawCertificateText = () => {
      // Use middle baseline to easily center text vertically
      ctx.textBaseline = "middle";
      
      // Name Position (in Yellow Bar)
      ctx.textAlign = "center";
      ctx.fillStyle = "#ffffff"; // White name
      ctx.font = "bold 42px 'Roboto', sans-serif"; // Reduced to 42px
      // Centered correctly in the yellow bar (Fixed at 840 per user correction)
      ctx.fillText(name.toUpperCase(), canvas.width / 2, 840);

      // Details Section (in dark green box)
      // Labels and colons end around X=750. Values start from X=780.
      const valX = 780;
      ctx.fillStyle = "#ffffff"; 
      ctx.font = "bold 36px 'Roboto', sans-serif"; // Ensure Roboto
      ctx.textAlign = "left";

      // Perfectly aligned vertical center positions (spaced by 63px, starting at 980)
      // 1. BIB NUMBER
      ctx.fillText(bibNumber || "-", valX, 980);
      
      // 2. DISTANCE
      ctx.fillText(category || "-", valX, 1043);
      
      // 3. GENDER
      const genderStr = gender === "M" ? "MALE" : gender === "F" ? "FEMALE" : (gender || "-");
      ctx.fillText(genderStr, valX, 1106);
      
      // 4. CATEGORY
      ctx.fillText(categoryDisplay || category || "-", valX, 1169);

      // 5. OVERALL POSITION
      ctx.fillText(ranking || "-", valX, 1232);

      // 6. CATEGORY POSITION 
      ctx.fillText(ranking || "-", valX, 1295);

      // 7. GENDER POSITION
      ctx.fillText(genderRanking || "-", valX, 1358);

      // Finish Time Position (in Black Box at bottom)
      ctx.textAlign = "center";
      ctx.fillStyle = "#ffffff"; 
      ctx.font = "bold 44px 'Roboto', sans-serif"; // Reduced to 44px
      // Y=1530 is horizontally and vertically centered in the black box
      ctx.fillText(time || "-", 940, 1530);
    };

    const downloadCanvas = () => {
      const link = document.createElement("a");
      link.download = `CERTIFICATE-${selectedEvent}-${name.replace(/\s+/g, "_")}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    };

    const drawFallbackCertificate = () => {
      canvas.width = 2400;
      canvas.height = 1700;

      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, "#f8fafc");
      gradient.addColorStop(1, "#f1f5f9");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.strokeStyle = "#0891b2";
      ctx.lineWidth = 24;
      ctx.strokeRect(60, 60, canvas.width - 120, canvas.height - 120);

      ctx.fillStyle = "#0f172a";
      ctx.textAlign = "center";
      ctx.font = "bold 88px 'Roboto', sans-serif";
      ctx.fillText("MOUNTAIN MARATHON FESTIVAL", canvas.width / 2, 240);
      ctx.font = "52px 'Roboto', sans-serif";
      ctx.fillText("CERTIFICATE OF ACHIEVEMENT", canvas.width / 2, 330);

      drawCertificateText();
      downloadCanvas();
    };

    const template = new Image();
    // Dynamically load the certificate template based on the event (BMM, JMM, SMM)
    template.src = `/certificate/${selectedEvent}.png`;

    template.onload = async () => {
      canvas.width = template.width;
      canvas.height = template.height;

      ctx.drawImage(template, 0, 0);

      // Ensure Roboto is loaded before drawing to canvas
      try {
        await document.fonts.load("bold 42px Roboto");
        await document.fonts.load("bold 36px Roboto");
        await document.fonts.load("bold 44px Roboto");
      } catch (err) {
        console.warn("Font loading failed, drawing with fallbacks", err);
      }

      drawCertificateText();
      downloadCanvas();
    };

    template.onerror = (e) => {
      console.error(`Error loading certificate template for ${selectedEvent}:`, e);
      // Fallback: Try a generic name if specific one fails, otherwise fallback to generated one
      if (template.src !== window.location.origin + "/certificate/BMM.png") {
          template.src = "/certificate/BMM.png";
      } else {
          drawFallbackCertificate();
      }
    };
  };

  const isMixGroup =
    categoryDisplay?.toLowerCase().includes("mix") ||
    String(category).toLowerCase().includes("mix");

  return (
    <section className="min-h-screen py-20 bg-white">
      <div className="container mx-auto px-4">
        <button
          onClick={() => navigate(backTo || `/results/${selectedEvent}`)}
          className="mb-6 flex items-center text-slate-700 hover:text-slate-900 transition-colors font-oxanium text-sm"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back to Race Results
        </button>
        <h1 className="text-2xl font-bold text-slate-800 mb-6">
          {selectedEvent} Race Results
        </h1>
        <div className="flex flex-col md:flex-row gap-4">
          {/* Kolom Kiri (30%) - Informasi */}
          <div className="w-full md:w-[30%] bg-white border rounded-2xl border-gray-300 shadow-sm flex flex-col items-center text-center">
            {/* Nama */}
            <h3 className="text-xl font-bold text-slate-800 uppercase tracking-wide py-4 w-full">
              {name}
            </h3>

            {/* Nomor BIB dengan latar hijau muda dan border lime */}
            <div className="bg-slate-50 text-slate-700 text-4xl font-extrabold py-3 w-full  border-y border-gray-200">
              {bibNumber || "-"}
            </div>

            {/* Kategori & finish time */}
            <div className="py-3 border-b border-gray-200 w-full">
              <p className="text-base font-semibold text-gray-800">
                {categoryDisplay || category || "-"}
              </p>
              <p className="text-sm font-bold text-slate-700">Finish Time: {time || "-"}</p>
            </div>

            {/* Detail info */}
            <div className="text-sm text-gray-700 py-3 space-y-1 w-full">
              <p>
                <span className="font-semibold">Gender :</span>{" "}
                {formatGender(gender, isMixGroup)}
              </p>
              <p>
                <span className="font-semibold">Category :</span>{" "}
                {categoryDisplay || category || "-"}
              </p>
            </div>
          </div>

          {/* Kolom Kanan (70%) - Tabel Detail dan Tombol Print Certificate */}
          <div className="w-full md:w-[70%] bg-white rounded-xl border border-gray-300 overflow-hidden flex flex-col">
            {/* Mobile: key-value grid */}
            <div className="sm:hidden grid grid-cols-2 divide-x divide-y divide-gray-200 border-b border-gray-200">
              <div className="px-4 py-3 flex flex-col items-center text-center">
                <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">Overall Rank</span>
                <span className="text-sm font-bold text-gray-800">{ranking ? `${ranking} All` : "-"}</span>
              </div>
              <div className="px-4 py-3 flex flex-col items-center text-center">
                <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">Gender Rank</span>
                <span className="text-sm font-bold text-gray-800">
                  {genderRanking
                    ? `${genderRanking} ${gender === "M" ? "Male" : gender === "F" ? "Female" : gender || ""}`
                    : "-"}
                </span>
              </div>
              {selectedEvent !== "SMM" && (
                <div className="px-4 py-3 flex flex-col items-center text-center">
                  <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">Country</span>
                  <span className="inline-flex items-center gap-1 text-sm text-gray-700">
                    <img
                      src={`https://flagicons.lipis.dev/flags/4x3/${getFlagCode(nationality)}.svg`}
                      className="w-4 h-3 shrink-0 border border-gray-200 rounded"
                      alt={nationality}
                    />
                    {nationality || "-"}
                  </span>
                </div>
              )}
              <div className="px-4 py-3 flex flex-col items-center text-center">
                <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">Gender</span>
                <span className="text-sm text-gray-700">{formatGender(gender, isMixGroup)}</span>
              </div>
              {selectedEvent !== "BMM" && (
                <div className="px-4 py-3 flex flex-col items-center text-center col-span-2">
                  <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">Status</span>
                  <span className="bg-green-50 border border-green-400 text-green-700 rounded-full px-3 py-0.5 text-xs">
                    {status || "-"}
                  </span>
                </div>
              )}
            </div>

            {/* Desktop: table */}
            <table className="hidden sm:table w-full table-auto divide-y divide-gray-200 mb-6">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Overall Rank
                  </th>
                  <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gender Rank
                  </th>
                  {selectedEvent !== "SMM" && (
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Country
                    </th>
                  )}
                  <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gender
                  </th>
                  {selectedEvent !== "BMM" && (
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-2 py-4 whitespace-nowrap text-xs md:text-sm text-gray-600 font-bold text-center">
                    {ranking ? `${ranking} All` : "-"}
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap text-xs md:text-sm text-gray-600 font-bold text-center">
                    {genderRanking
                      ? `${genderRanking} ${
                          gender
                            ? gender === "M"
                              ? "Male"
                              : gender === "F"
                              ? "Female"
                              : gender
                            : "-"
                        }`
                      : "-"}
                  </td>
                  {selectedEvent !== "SMM" && (
                    <td className="px-2 py-4 text-sm text-gray-600 text-center">
                      <span className="border border-gray-300 rounded-full px-2 py-1 inline-flex items-center gap-1 whitespace-nowrap">
                        <img
                          src={`https://flagicons.lipis.dev/flags/4x3/${getFlagCode(nationality)}.svg`}
                          className="country-flag w-4 h-3 shrink-0 border border-gray-300 rounded"
                          alt={nationality}
                        />
                        <span className="text-xs">{nationality || "-"}</span>
                      </span>
                    </td>
                  )}
                  <td className="px-2 py-4 text-sm text-gray-600 text-center">
                    <span className="border border-gray-300 rounded-full px-2 py-1 inline-flex items-center gap-1 whitespace-nowrap">
                      {formatGender(gender, isMixGroup)}
                    </span>
                  </td>
                  {selectedEvent !== "BMM" && (
                    <td className="px-2 py-4 text-xs text-green-700 text-center">
                      <span className="bg-green-50 border border-green-400 rounded-full px-2 py-1">
                        {status || "-"}
                      </span>
                    </td>
                  )}
                </tr>
              </tbody>
            </table>

            <div className="text-center px-4 pb-4 mt-4">
              <Button
                onClick={handlePrintCertificate}
                disabled={selectedEvent === "BMM" && !certificateUrl}
                className="w-full"
              >
                <FontAwesomeIcon
                  icon={faFileLines}
                  className={`${
                    selectedEvent === "BMM" && !certificateUrl
                      ? "text-gray-500"
                      : "text-white"
                  } w-4 h-4`}
                />
                {certificateUrl ? "Download Certificate" : "Print Certificate"}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
    </section>
  );
};

export default ResultsDetail;
